import React from "react";
import { differenceInHours, parseISO } from "date-fns";
import FlightSegment from "./segments/FlightSegment";
import HotelSegment from "./segments/HotelSegment";
import NoSegmentAvailable from "./segments/NoSegmentAvailable";
import DefaultSegment from "./segments/DefaultSegment";
import { TripElement } from "@/types/trip";
import CompletedTripSegment from "./segments/CompletedTripSegment";

interface TripCardSegmentProps {
  isPastTrip: boolean;
  nextSegment: TripElement | null;
  getAirportInfo: (element: any, type: 'departure' | 'arrival') => any;
  handleCheckFlightStatus?: (flightNumber: string) => void;
  tripId: string;
  destinationName: string;
}

const TripCardSegment: React.FC<TripCardSegmentProps> = ({
  isPastTrip,
  nextSegment,
  getAirportInfo,
  handleCheckFlightStatus,
  tripId,
  destinationName
}) => {
  if (isPastTrip) {
    return <CompletedTripSegment destinationName={destinationName} />;
  }

  if (!nextSegment) {
    return <NoSegmentAvailable />;
  }

  // For flight segments
  if (nextSegment.type === "flight") {
    const flightDetails = nextSegment.details as any;
    const departureTime = parseISO(flightDetails.departure.time || "1970");
    const isWithin72Hours = differenceInHours(departureTime, new Date()) <= 72;
    const isWithin48Hours = differenceInHours(departureTime, new Date()) <= 48;

    // Log confirmation numbers for debugging
    console.debug('Flight confirmation numbers:', flightDetails.confirmationNumbers);

    // Check if this trip has only one flight segment
    // This is used to determine if we should show "Direct" label
    const isDirectFlight = true; // Default to true for trip card view

    return (
      <FlightSegment
        flightDetails={flightDetails}
        departureAirport={getAirportInfo(nextSegment, "departure")}
        arrivalAirport={getAirportInfo(nextSegment, "arrival")}
        isWithin72Hours={isWithin72Hours}
        isWithin48Hours={isWithin48Hours}
        handleCheckFlightStatus={handleCheckFlightStatus}
        tripId={tripId}
        destinationName={destinationName}
        showAddToCalendarButton={false} // Hide in trip card view
        showETicketButton={true} // Always show E-Ticket button when available
        isDirectFlight={isDirectFlight} // Pass isDirectFlight prop
      />
    );
  }

  // For hotel segments
  if (nextSegment.type === "hotel") {
    const hotelDetails = nextSegment.details as any;
    return (
      <HotelSegment 
        hotelDetails={{
          name: hotelDetails.name,
          city: hotelDetails.city,
          checkIn: hotelDetails.checkIn,
          checkOut: hotelDetails.checkOut
        }}
        startDate={nextSegment.startDate}
        endDate={nextSegment.endDate}
      />
    );
  }

  // For any other segment type
  return <DefaultSegment segment={nextSegment} />;
};

export default TripCardSegment;
